<template>
    <label class="cr:relative">
        <span v-if="label" class="cr:block cr:mb-1 cr:text-sm cr:font-medium">{{ label }}</span>
        <slot v-else class="cr:mb-1"></slot>
        <div class="cr:flex">
            <slot name="drop-down"/> <!-- Slot for visually integrating a drop-down with the input field -->
            <input ref="inputElement"
                   :value="modelValue"
                   :type="inputType"
                   @input="updateValue"
                   :name="name"
                   :title="title"
                   :id="id"
                   :placeholder="placeholder"
                   :minlength="minLength"
                   :maxlength="maxLength || ''"
                   :required="isRequired()"
                   :autocomplete="autocomplete"
                   :disabled="inputDisabled"
                   class="cr:w-full cr:block cr:placeholder:font-light cr:font-normal cr:placeholder:text-slate-400 cr:focus:outline-none cr:bg-white cr:border cr:border-slate-300 cr:rounded-lg cr:py-2 cr:px-4 cr:h-11"
                   :class="[classes, {'cr:bg-slate-100 cr:text-slate-400': inputDisabled, 'cr:bg-none': !inputDisabled}, activeClasses]"
                   oninvalid="this.setCustomValidity(' ')"
                   oninput="this.setCustomValidity('')"
            />
        </div>
    </label>
</template>

<script setup lang="ts">

import {computed, ref} from "vue";
import {useRegistrationStore} from "@/stores/registration.ts";
import {ORIGINS} from "@/services/ConstantSerice.ts";

const inputElement = ref(null);
const registrationStore = useRegistrationStore();

const props = defineProps({
    name: {
        type: String,
        default: ''
    },
    title: {
        type: String,
        default: ''
    },
    id: {
        type: String,
        default: ''
    },
    placeholder: {
        type: String,
        default: '',
    },
    classes: {
        type: String,
        default: '',
    },
    modelValue: {
        type: String,
        default: '',
    },
    label: {
        type: String,
        default: '',
    },
    inputType: {
        type: String,
        validator: (val: string) => ['text', 'number', 'email', 'password', 'tel', 'url'].includes(val),
        default: 'text',
    },
    requiredField: {
        type: [Boolean, null],
        default: null,
    },
    minLength: {
        type: Number,
        default: 0,
    },
    maxLength: {
        type: Number,
        default: null
    },
    inputDisabled: {
        type: Boolean,
        default: false,
    },
    autocomplete: {
        type: String,
        default: 'off',
    },
});

const updateValue = (event: any) => {
    emit('update:modelValue', event.target.value)
}

const emit = defineEmits([
    'update:modelValue',
]);

/**
 * Fields with labels appended with a * are auto-required
 * @returns {boolean}
 */
const isRequired = () => props.requiredField || /\*$/.test(props.label);

const activeClasses = computed(() => {
    switch (registrationStore.registrationOrigin?.toLocaleLowerCase()) {
        case ORIGINS.BE:
            return 'cr:focus:border-[var(--be-primary)]';
        case ORIGINS.KE:
            return 'cr:focus:border-[var(--ke-primary)]';
        case ORIGINS.FIXR:
            return 'cr:focus:border-[var(--fixr-primary)]';
        case ORIGINS.SR:
            return 'cr:focus:border-[var(--sr-primary)]';
        case ORIGINS.RC:
            return 'cr:focus:border-[var(--rc-primary)]';
        case ORIGINS.HM:
            return 'cr:focus:border-[var(--hm-primary)]';
        default:
            return 'cr:focus:border-[var(--default-primary)]';
    }
});

</script>

<style scoped>

</style>
